import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSubscription } from "../contexts/SubscriptionContext";
import { ROUTE_SCREEN_NAMES, EVENTS } from "./events";
import {
  queueEvent,
  setAnalyticsUserId,
  setAnalyticsUserProperties,
  startSession,
  endSession,
  trackScreenView,
} from "./index";

// Everything the app does in App.js (NavigationContainer onStateChange +
// AppState listener + setAnalyticsUserId on login), done once for the web:
//   - screen_viewed on every route change
//   - session_started / session_ended on tab visibility
//   - Firebase user id from the auth context, tier user property from the
//     subscription context (+ subscription_tier_changed on transitions)
//
// Rendered inside the router AND inside Auth/Subscription providers.

const HEX24 = /^[0-9a-f]{24}$/i;

export function resolveScreenName(pathname) {
  const known = ROUTE_SCREEN_NAMES[pathname];
  if (known) return known;

  const seg = pathname.split("/").filter(Boolean);
  if (seg[0] === "app") {
    if (seg[1] === "games" && seg[3] === "edit") return "game_edit";
    if (seg[1] === "games" && seg[2]) return "game_menu";
    if (seg[1] === "modes" && seg[2]) return "mode_edit";
    if (seg[1] === "users" && seg[2]) return "user_profile";
  }
  if (seg[0] === "game-menu") return "web_game_menu";
  if (seg[0] === "custom-mode") return "custom_mode_question";
  if (seg[0] === "question") return "question";
  if (seg[0] === "word") return "word";
  if (seg[0] === "gift-code") return "gift_code";
  if (seg[0] === "partner") return seg.length > 1 ? "partner_game" : "partner_landing";
  if (seg[0] === "live") return "live_spectate";
  // "/:code" and "/:code/end" — the greedy public game display routes.
  if (seg.length === 1) return "web_game";
  if (seg.length === 2 && seg[1] === "end") return "web_game_end";

  return pathname
    .split("/")
    .map((s) => (HEX24.test(s) ? ":id" : s))
    .join("/")
    .slice(0, 100);
}

const AnalyticsBridge = () => {
  const { user } = useAuth();
  const { tier, loading: subLoading } = useSubscription();
  const location = useLocation();

  const previousScreenRef = useRef(null);
  const userIdRef = useRef(null);
  const tierRef = useRef(null);

  // Session lifecycle — a hidden tab ends the session, re-showing starts a new
  // one (the web equivalent of the app's background/foreground AppState).
  useEffect(() => {
    startSession("cold_start");

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") endSession();
      else startSession("foreground");
    };
    const onPageHide = () => endSession();

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);

  // screen_viewed on every route change (pathname only — query strings would
  // blow past Firebase's 100-char param limit and add no funnel value).
  useEffect(() => {
    const screen = resolveScreenName(location.pathname);
    if (screen === previousScreenRef.current) return;
    trackScreenView(screen, previousScreenRef.current);
    previousScreenRef.current = screen;
  }, [location.pathname]);

  // Firebase user id follows the logged-in account (null on logout).
  useEffect(() => {
    const id = user?._id ? String(user._id) : null;
    if (id === userIdRef.current) return;
    userIdRef.current = id;
    setAnalyticsUserId(id);
  }, [user?._id]);

  // Tier user property + transition event, once the subscription resolves.
  useEffect(() => {
    if (subLoading) return;
    if (tier === tierRef.current) return;
    const previous = tierRef.current;
    tierRef.current = tier;
    setAnalyticsUserProperties({ tier, paid_version: false });
    if (previous) {
      queueEvent(EVENTS.SUBSCRIPTION_TIER_CHANGED, { from: previous, to: tier });
    }
  }, [tier, subLoading]);

  return null;
};

export default AnalyticsBridge;
